import { useState } from "react";
import type { Bestellgruppe } from "../../api/bestellgruppen.ts";
import type { Order } from "../../lib/types.ts";

export interface BestellgruppenBereichProps {
  bestellung: Order;
  bestellungen: readonly Order[];
  gruppe: Bestellgruppe | null;
  onBestellungOeffnen?: (orderId: string) => void;
  onGruppenansichtOeffnen?: (groupId: string) => void;
  onMitgliedHinzufuegen?: (orderId: string) => void;
  onMitgliedLoesen?: (orderId: string) => void;
  onGruppeAufloesen?: (retainedOrderIds: string[]) => void;
  /** In der eigenständigen Ansicht dürfen andere Mitglieder verwaltet werden, im Drawer nicht. */
  gruppenansicht?: boolean;
  /** Die portale Ansicht reicht ihr bestehendes Bearbeitungsrecht hierher durch. */
  darfBearbeiten?: boolean;
}

function kennung(bestellung: Order | undefined, orderId: string): string {
  return String(bestellung?.orderNo || bestellung?.name || orderId);
}

function Linkfolgen({
  gruppe,
  verliert,
  behaelt,
}: {
  gruppe: Bestellgruppe;
  verliert: readonly string[];
  behaelt: readonly string[];
}) {
  if (!gruppe.documents.length) return <p>Es gibt keine Gruppendokumente.</p>;
  return (
    <ul aria-label="Sichtbarkeit der Gruppendokumente">
      {gruppe.documents.flatMap((dokument) => [
        ...verliert.map((orderId) => <li key={`${dokument.documentId}-verliert-${orderId}`}>{`${orderId} verliert Dokument ${dokument.docType}.`}</li>),
        ...behaelt.map((orderId) => <li key={`${dokument.documentId}-behaelt-${orderId}`}>{`${orderId} behält Dokument ${dokument.docType}.`}</li>),
      ])}
    </ul>
  );
}

export function BestellgruppenBereich({
  bestellung,
  bestellungen,
  gruppe,
  onBestellungOeffnen,
  onGruppenansichtOeffnen,
  onMitgliedHinzufuegen,
  onMitgliedLoesen,
  onGruppeAufloesen,
  gruppenansicht = false,
  darfBearbeiten = true,
}: BestellgruppenBereichProps) {
  const [hinzufuegenOffen, setHinzufuegenOffen] = useState(false);
  const [auswahl, setAuswahl] = useState("");
  const [loesenId, setLoesenId] = useState<string | null>(null);
  const [aufloesenOffen, setAufloesenOffen] = useState(false);
  const [retained, setRetained] = useState<string[]>([]);

  if (!gruppe) return null;

  const mitglieder = gruppe.members;
  const aktiveMitglieder = gruppe.members.filter((mitglied) => mitglied.membershipState === "active");
  const istHistorisch = gruppe.status === "dissolved";
  const mitgliedIds = new Set(mitglieder.map((mitglied) => mitglied.orderId));
  const hinzufuegbar = bestellungen.filter(
    (eintrag) =>
      !mitgliedIds.has(String(eintrag.id)) &&
      eintrag.orderGroupStatus !== "active" &&
      Number(eintrag.kLieferant) === gruppe.kLieferant,
  );
  const loesenBestellung = bestellungen.find((eintrag) => String(eintrag.id) === loesenId);
  const andereAktive = aktiveMitglieder.filter((mitglied) => mitglied.orderId !== loesenId);

  const zeigeFolgen = (verliert: readonly string[], behaelt: readonly string[]) => (
    <Linkfolgen
      gruppe={gruppe}
      verliert={verliert.map((orderId) => kennung(bestellungen.find((eintrag) => String(eintrag.id) === orderId), orderId))}
      behaelt={behaelt.map((orderId) => kennung(bestellungen.find((eintrag) => String(eintrag.id) === orderId), orderId))}
    />
  );

  return (
    <section className="bestellgruppen-bereich" aria-label="Zusammengehörige Bestellungen">
      <h3>Zusammengehörige Bestellungen</h3>
      <p>{`${mitglieder.length} Bestellungen · Lieferant ${gruppe.kLieferant}`}</p>
      {istHistorisch ? <p role="status">Aufgelöst · historisch</p> : null}
      <ul>
        {mitglieder.map((mitglied) => {
          const eintrag = bestellungen.find((bestellung) => String(bestellung.id) === mitglied.orderId);
          const label = kennung(eintrag, mitglied.orderId);
          return (
            <li key={mitglied.orderId}>
              <span data-permission="view_board"><button type="button" onClick={() => onBestellungOeffnen?.(mitglied.orderId)} aria-label={`${label} öffnen`}>
                {label}
              </button></span>
              {mitglied.membershipState === "removed" ? <span> · historisch entfernt</span> : null}
              {!istHistorisch && darfBearbeiten && mitglied.membershipState === "active" && (gruppenansicht || mitglied.orderId === String(bestellung.id)) ? (
                <span data-permission="edit_orders"><button type="button" onClick={() => setLoesenId(mitglied.orderId)} aria-label={`${label} aus Gruppe lösen`}>
                  Aus Gruppe lösen
                </button></span>
              ) : null}
            </li>
          );
        })}
      </ul>
      <span data-permission="view_board"><button type="button" onClick={() => onGruppenansichtOeffnen?.(gruppe.id)}>Gruppe öffnen</button></span>
      {!istHistorisch && darfBearbeiten ? (
        <>
          <span data-permission="edit_orders"><button type="button" onClick={() => setHinzufuegenOffen(true)} disabled={!hinzufuegbar.length}>
            Bestellung hinzufügen
          </button></span>
          <span data-permission="edit_orders"><button type="button" onClick={() => setAufloesenOffen(true)}>Gruppe auflösen</button></span>
        </>
      ) : null}

      {hinzufuegenOffen ? (
        <section role="dialog" aria-label="Bestellung zur Gruppe hinzufügen" data-permission="edit_orders">
          <label>
            Ungruppierte Bestellung
            <select value={auswahl} onChange={(ereignis) => setAuswahl(ereignis.target.value)}>
              <option value="">Bitte wählen</option>
              {hinzufuegbar.map((eintrag) => <option key={String(eintrag.id)} value={String(eintrag.id)}>{kennung(eintrag, String(eintrag.id))}</option>)}
            </select>
          </label>
          <button type="button" onClick={() => setHinzufuegenOffen(false)}>Abbrechen</button>
          <button type="button" disabled={!auswahl} onClick={() => { onMitgliedHinzufuegen?.(auswahl); setHinzufuegenOffen(false); }}>Hinzufügen</button>
        </section>
      ) : null}

      {loesenId ? (
        <section role="dialog" aria-label="Mitglied aus Bestellgruppe lösen" data-permission="edit_orders">
          <h4>{`${kennung(loesenBestellung, loesenId)} aus Gruppe lösen`}</h4>
          {zeigeFolgen([loesenId], aktiveMitglieder.length === 2 ? andereAktive.map((mitglied) => mitglied.orderId) : [])}
          <button type="button" onClick={() => setLoesenId(null)}>Abbrechen</button>
          <button type="button" onClick={() => { onMitgliedLoesen?.(loesenId); setLoesenId(null); }}>Lösen bestätigen</button>
        </section>
      ) : null}

      {aufloesenOffen ? (
        <section role="dialog" aria-label="Bestellgruppe auflösen" data-permission="edit_orders">
          <h4>Gruppe auflösen</h4>
          <p>Wähle mindestens eine Bestellung, die ihre Gruppendokumente behält.</p>
          {aktiveMitglieder.map((mitglied) => {
            const label = kennung(bestellungen.find((eintrag) => String(eintrag.id) === mitglied.orderId), mitglied.orderId);
            const checked = retained.includes(mitglied.orderId);
            return (
              <label key={mitglied.orderId}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => setRetained((vorher) => checked ? vorher.filter((id) => id !== mitglied.orderId) : [...vorher, mitglied.orderId])}
                />
                {`${label} als Dokument-Aufbewahrung behalten`}
              </label>
            );
          })}
          {zeigeFolgen(aktiveMitglieder.map((mitglied) => mitglied.orderId).filter((id) => !retained.includes(id)), retained)}
          <button type="button" onClick={() => { setAufloesenOffen(false); setRetained([]); }}>Abbrechen</button>
          <button type="button" disabled={!retained.length} onClick={() => { onGruppeAufloesen?.(retained); setAufloesenOffen(false); setRetained([]); }}>Auflösung bestätigen</button>
        </section>
      ) : null}
    </section>
  );
}
